import { eachYearOfInterval, format } from "date-fns";
import { IconButton, Slider, Stack } from "@mui/material";
import { FastRewindOutlined, FastForwardOutlined } from "@mui/icons-material";
import { useEffect, useState } from "react";
import useQueryState from "../../hooks/useQueryState";
import useYearRange from "../../hooks/useYearRange";

const getYearsArray = (startYear: number, endYear: number) => {
  const startDate = new Date(startYear, 0);
  const endDate = new Date(endYear, 0);

  return eachYearOfInterval({ start: startDate, end: endDate })
    .map((year) => format(year, "yyyy"))
    .reverse();
};

const SliderView = () => {
  const {
    getYearRange: { data: yearRange },
  } = useYearRange();
  const {
    queryState: { year },
    setQueryState,
  } = useQueryState();

  const [years, setYears] = useState<string[]>([]);
  const [labelInterval, setLabelInterval] = useState(5);

  useEffect(() => {
    if (yearRange) {
      const obsYears = getYearsArray(
        yearRange.data.startYear ?? 2000,
        yearRange.data.endYear ?? 2026,
      );
      setQueryState({
        year: obsYears.includes(`${year}`) ? year : yearRange.data.endYear,
      });
      setYears(obsYears);
    } else {
      const obsYears = getYearsArray(2000, 2026);
      if (!year) {
        setQueryState({ year: Number(obsYears[0]) });
      }
      setYears(obsYears);
    }
  }, [yearRange]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 500) setLabelInterval(5);
      else if (width < 800) setLabelInterval(4);
      else if (width < 1200) setLabelInterval(3);
      else if (width < 1500) setLabelInterval(2);
      else setLabelInterval(1);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const marks = years.map((year, index) => ({
    value: Number(year),
    label: index % labelInterval === 0 ? year : "",
  }));

  const handleChange = (event: Event, newValue: number | number[]) => {
    if (typeof newValue === "number") {
      setQueryState({ year: newValue });
    }
  };

  const handleSelectYear = (type: string) => {
    const addedYear = type === "next" ? 1 : -1;

    const updatedYear = Number(year) + addedYear;
    setQueryState({ year: updatedYear });
  };

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      borderTop="1px solid #CFDBE2"
      gap={1}
    >
      <Stack direction="row">
        <IconButton
          onClick={() => handleSelectYear("previous")}
          disabled={Number(year) === Number(years[years.length - 1])}
        >
          <FastRewindOutlined fontSize="small" />
        </IconButton>
        <IconButton
          onClick={() => handleSelectYear("next")}
          disabled={Number(year) === Number(years[0])}
        >
          <FastForwardOutlined fontSize="small" />
        </IconButton>
      </Stack>
      <Stack px={1} flex={1} pr={3}>
        <Slider
          sx={{
            scrollbarColor: "white",
            backgroundColor: "white",
            backgroundSize: "auto",
            "& .MuiSlider-markLabel": {
              fontSize: "10px",
            },
          }}
          value={Number(year)}
          onChange={handleChange}
          valueLabelDisplay="auto"
          step={1}
          marks={marks}
          min={Number(years[years.length - 1])}
          max={Number(years[0])}
        />
      </Stack>
    </Stack>
  );
};

export default SliderView;
